import { Button } from '@apps/web/components/ui/button';
import { useTrainingCoverageDashboard } from '@apps/web/features/training_coverage/model/use_training_coverage_dashboard';
import { LearnedNotesList } from '@apps/web/features/training_coverage/ui/learned_notes_list';
import { toast } from '@apps/web/lib/app_notifications';
import { type TrainingCoverageResponse } from '@libraries/typescript/api_client';

// The only banner is actionable: after a week with barely any replies, the
// fix is a human introduction, so it hands the coordinator a message to
// send from their own number. The first quiet days need no announcement —
// the empty notes list already says it.
const LOW_ADOPTION_MIN_DAYS = 7;
const LOW_ADOPTION_MAX_REPLY_SHARE = 0.25;

const INTRO_MESSAGE =
    'Hi team! We’re working with Phoebe, our new scheduling assistant. ' +
    'Phoebe texts about shifts and availability — replies go straight ' +
    'to our schedule, so please answer when you can. Thank you!';

// Caregivers who have shared anything at all (availability, memory, or both).
function caregiversWithAnyData(data: TrainingCoverageResponse): number {
    return (
        data.caregivers_with_availability +
        data.caregivers_with_memory -
        data.caregivers_known_well
    );
}

function lowAdoptionNudge(data: TrainingCoverageResponse): string | null {
    const day = data.days_since_start;
    if (day === null || day === undefined) {
        return null;
    }
    const anyData = caregiversWithAnyData(data);
    if (
        day >= LOW_ADOPTION_MIN_DAYS &&
        data.active_caregiver_count > 0 &&
        anyData / data.active_caregiver_count < LOW_ADOPTION_MAX_REPLY_SHARE
    ) {
        return `Only ${anyData} of ${data.active_caregiver_count} caregivers have shared anything with Phoebe so far. A quick mention from you goes a long way — caregivers respond far more once a manager makes the introduction.`;
    }
    return null;
}

function copyIntroMessage(): void {
    void (async () => {
        try {
            await navigator.clipboard.writeText(INTRO_MESSAGE);
        } catch {
            toast.error('Couldn’t copy the message.', {
                persistNotification: false,
            });
            return;
        }
        toast.success('Intro message copied.', {
            persistNotification: false,
        });
    })();
}

const NUDGE_TINT = '#F3B23E';

function NudgeCallout({ text }: { readonly text: string }) {
    return (
        <div
            className="flex flex-wrap items-center gap-3 rounded-xl border p-3"
            style={{
                borderColor: `color-mix(in srgb, ${NUDGE_TINT} 45%, transparent)`,
                background: `color-mix(in srgb, ${NUDGE_TINT} 10%, transparent)`,
            }}
        >
            <p className="min-w-0 flex-1 text-sm">{text}</p>
            <Button
                variant="secondary"
                size="sm"
                className="shrink-0"
                onClick={copyIntroMessage}
            >
                Copy an intro message
            </Button>
        </div>
    );
}

export function CoverageSection({
    organizationId,
    isAdmin,
}: {
    readonly organizationId: string | null;
    readonly isAdmin: boolean;
}) {
    const { isLoading, error, data } = useTrainingCoverageDashboard({
        organizationId,
        isAdmin,
    });

    if (isLoading) {
        return (
            <p className="text-muted-foreground text-sm">Loading coverage…</p>
        );
    }
    if (error) {
        return (
            <div>
                <p className="text-sm font-medium">We couldn’t load coverage</p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                    Refresh the page to try again.
                </p>
            </div>
        );
    }
    if (!data) {
        return null;
    }

    const nudge = lowAdoptionNudge(data);
    const caregiversKnown = data.caregivers_known_well;
    const factsLearned = data.metrics.facts_learned;

    return (
        // ph-no-capture: the notes carry caregiver and client names plus
        // learned-fact text; keep them out of DOM autocapture.
        <section
            className="ph-no-capture min-w-0"
            data-testid="teach-phoebe-coverage"
        >
            <h2 className="text-muted-foreground border-b pb-2 text-xs font-semibold tracking-wider uppercase">
                What Phoebe has learned
            </h2>
            <div className="flex flex-col gap-4 pt-4">
                {nudge ? <NudgeCallout text={nudge} /> : null}
                <LearnedNotesList
                    highlights={data.highlights}
                    organizationId={organizationId}
                />
                {/* The tally stays quiet, and never leads with a zero — on
                    day one "0 of 384 caregivers known well" reads as failure. */}
                {caregiversKnown > 0 ? (
                    <p className="text-muted-foreground text-xs">
                        {caregiversKnown} of {data.active_caregiver_count}{' '}
                        caregivers known well
                        {data.clients_known > 0
                            ? ` · ${data.clients_known} clients known`
                            : ''}
                        {factsLearned > 0
                            ? ` · ${factsLearned} facts learned`
                            : ''}
                    </p>
                ) : null}
            </div>
        </section>
    );
}
